import { useState, type FormEvent } from 'react';
import {
  useDiscountCodes,
  type DiscountCode,
  type DiscountInput,
} from '@/hooks/useDiscountCodes';
import { useTours } from '@/hooks/useTours';
import SelectChevron from '@/components/base/SelectChevron';
import TourDiscountManager from './TourDiscountManager';

interface FormState {
  id: string | null;
  code: string;
  type: 'percent' | 'fixed';
  value: string;
  active: boolean;
  description: string;
  valid_until: string;
  usage_limit: string;
  tour_ids: string[];
}

const emptyForm: FormState = {
  id: null,
  code: '',
  type: 'percent',
  value: '',
  active: true,
  description: '',
  valid_until: '',
  usage_limit: '',
  tour_ids: [],
};

function formatDate(dateStr: string | null): string {
  if (!dateStr) return 'No expiry';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return 'No expiry';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function getWarnings(c: DiscountCode): string[] {
  const warnings: string[] = [];
  if (!c.active) return warnings;

  // Nearly out of uses (remaining <= 3)
  if (c.usage_limit != null) {
    const remaining = c.usage_limit - c.used_count;
    if (remaining > 0 && remaining <= 3) {
      warnings.push(
        `${remaining} use${remaining !== 1 ? 's' : ''} left`,
      );
    }
  }

  // Nearly expiring (within 3 days, not yet expired)
  if (c.valid_until) {
    const d = new Date(c.valid_until);
    if (!Number.isNaN(d.getTime())) {
      const diffDays = Math.ceil(
        (d.getTime() - Date.now()) / (1000 * 60 * 60 * 24),
      );
      if (diffDays >= 0 && diffDays <= 3) {
        warnings.push(
          diffDays === 0
            ? 'Expires today'
            : `Expires in ${diffDays} day${diffDays !== 1 ? 's' : ''}`,
        );
      }
    }
  }

  return warnings;
}

function DiscountViewSwitcher({
  view,
  onChange,
}: {
  view: 'codes' | 'tours';
  onChange: (v: 'codes' | 'tours') => void;
}) {
  return (
    <div className="inline-flex items-center gap-1 rounded-full border border-secondary-200 bg-white p-1">
      <button
        onClick={() => onChange('codes')}
        className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
          view === 'codes' ? 'bg-primary-500 text-white' : 'text-secondary-600 hover:text-secondary-900'
        }`}
      >
        Discount codes
      </button>
      <button
        onClick={() => onChange('tours')}
        className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
          view === 'tours' ? 'bg-primary-500 text-white' : 'text-secondary-600 hover:text-secondary-900'
        }`}
      >
        Tour price discounts
      </button>
    </div>
  );
}

export default function DiscountManager() {
  const { codes, loading, error, createCode, updateCode, toggleActive, deleteCode } =
    useDiscountCodes();
  const { tours } = useTours();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [toast, setToast] = useState('');
  const [view, setView] = useState<'codes' | 'tours'>('codes');

  const isEditing = !!form.id;

  const openCreate = () => {
    setForm(emptyForm);
    setFormError(null);
    setShowForm(true);
  };

  const openEdit = (c: DiscountCode) => {
    setForm({
      id: c.id,
      code: c.code,
      type: c.type,
      value: String(c.value),
      active: c.active,
      description: c.description || '',
      valid_until: c.valid_until ? c.valid_until.slice(0, 10) : '',
      usage_limit: c.usage_limit != null ? String(c.usage_limit) : '',
      tour_ids: c.tour_ids || [],
    });
    setFormError(null);
    setShowForm(true);
  };

  const toggleTour = (tourId: string) => {
    setForm((prev) => ({
      ...prev,
      tour_ids: prev.tour_ids.includes(tourId)
        ? prev.tour_ids.filter((id) => id !== tourId)
        : [...prev.tour_ids, tourId],
    }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError(null);

    const code = form.code.trim();
    const value = Number(form.value);
    const usageLimit = form.usage_limit.trim() === '' ? null : Number(form.usage_limit);

    if (!code) {
      setFormError('Please enter a discount code.');
      return;
    }
    if (!form.value || Number.isNaN(value) || value <= 0) {
      setFormError('Please enter a valid discount value greater than 0.');
      return;
    }
    if (form.type === 'percent' && value > 100) {
      setFormError('Percent discount cannot exceed 100%.');
      return;
    }
    if (
      usageLimit !== null &&
      (Number.isNaN(usageLimit) || usageLimit <= 0 || !Number.isInteger(usageLimit))
    ) {
      setFormError('Usage limit must be a positive whole number (or leave blank for unlimited).');
      return;
    }

    setSaving(true);
    const input: DiscountInput = {
      code,
      type: form.type,
      value,
      active: form.active,
      description: form.description,
      valid_until: form.valid_until,
      usage_limit: usageLimit,
      tour_ids: form.tour_ids,
    };

    const result = isEditing ? await updateCode(form.id as string, input) : await createCode(input);
    setSaving(false);

    if (result.error) {
      setFormError(result.error);
      return;
    }

    setShowForm(false);
    setToast(isEditing ? 'Discount code updated.' : 'Discount code created.');
    window.setTimeout(() => setToast(''), 3000);
  };

  const handleToggle = async (c: DiscountCode) => {
    setToast('');
    const { error: err } = await toggleActive(c.id, !c.active);
    setToast(
      err
        ? 'Failed to update. Please try again.'
        : `Code ${c.code} ${c.active ? 'deactivated' : 'activated'}.`,
    );
    window.setTimeout(() => setToast(''), 3000);
  };

  const handleDelete = async (c: DiscountCode) => {
    if (!window.confirm(`Delete discount code "${c.code}"? This cannot be undone.`)) return;
    setToast('');
    const { error: err } = await deleteCode(c.id);
    setToast(err ? 'Failed to delete. Please try again.' : `Code ${c.code} deleted.`);
    window.setTimeout(() => setToast(''), 3000);
  };

  if (view === 'tours') {
    return (
      <div className="space-y-6">
        <DiscountViewSwitcher view={view} onChange={setView} />
        <TourDiscountManager />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex flex-col items-center gap-3 text-secondary-500">
          <i className="ri-loader-4-line animate-spin text-3xl text-primary-500" />
          <span className="text-sm">Loading discount codes...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-16">
        <div className="w-16 h-16 mx-auto rounded-full bg-red-100 flex items-center justify-center">
          <i className="ri-error-warning-line text-2xl text-red-500" />
        </div>
        <h3 className="mt-4 font-heading text-lg font-bold text-secondary-900">Something went wrong</h3>
        <p className="mt-1 text-sm text-secondary-500">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <DiscountViewSwitcher view={view} onChange={setView} />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="font-heading text-lg font-bold text-secondary-900">Discount codes</h2>
          <p className="text-sm text-secondary-500 mt-0.5">
            Create codes travelers can apply at booking. {codes.length} code
            {codes.length !== 1 ? 's' : ''} total.
          </p>
        </div>
        <button
          onClick={openCreate}
          className="inline-flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 text-white font-semibold text-sm px-5 py-2.5 rounded-full transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className="ri-add-line text-base" />
          New discount code
        </button>
      </div>

      {toast && (
        <div className="flex items-center gap-2 text-sm text-primary-700 bg-primary-50 border border-primary-200 rounded-md px-4 py-3">
          <i className="ri-checkbox-circle-line text-base" />
          {toast}
        </div>
      )}

      {/* Codes list */}
      {codes.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-lg border border-secondary-200">
          <div className="w-16 h-16 mx-auto rounded-full bg-secondary-100 flex items-center justify-center">
            <i className="ri-coupon-3-line text-2xl text-secondary-500" />
          </div>
          <h3 className="mt-4 font-heading text-lg font-bold text-secondary-900">
            No discount codes yet
          </h3>
          <p className="mt-1 text-sm text-secondary-500">
            Create your first code to start offering discounts at checkout.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {codes.map((c) => (
            <div
              key={c.id}
              className="bg-white rounded-lg border border-secondary-200 p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-4"
            >
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono font-bold text-base text-secondary-900 tracking-widest">
                    {c.code}
                  </span>
                  <span
                    className={`text-xs font-semibold rounded-full px-2.5 py-0.5 whitespace-nowrap ${
                      c.type === 'percent'
                        ? 'bg-primary-100 text-primary-700'
                        : 'bg-accent-100 text-accent-900'
                    }`}
                  >
                    {c.type === 'percent' ? `${c.value}% off` : `$${Number(c.value).toLocaleString()} off`}
                  </span>
                  <span
                    className={`text-xs font-semibold rounded-full px-2.5 py-0.5 whitespace-nowrap ${
                      c.active ? 'bg-green-100 text-green-700' : 'bg-secondary-100 text-secondary-500'
                    }`}
                  >
                    {c.active ? 'Active' : 'Inactive'}
                  </span>
                  {getWarnings(c).map((warning) => (
                    <span
                      key={warning}
                      className="inline-flex items-center gap-1 text-xs font-semibold rounded-full px-2.5 py-0.5 whitespace-nowrap bg-amber-100 text-amber-700"
                    >
                      <i className="ri-alert-line text-xs" />
                      {warning}
                    </span>
                  ))}
                </div>
                {c.description && (
                  <p className="mt-1.5 text-sm text-secondary-500">{c.description}</p>
                )}
                <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-secondary-400">
                  <span>Expires: {formatDate(c.valid_until)}</span>
                  <span>
                    Used: {c.used_count}
                    {c.usage_limit != null ? ` / ${c.usage_limit}` : ' / ∞'}
                  </span>
                  <span>
                    {c.tour_ids && c.tour_ids.length > 0
                      ? `Applies to ${c.tour_ids.length} tour${c.tour_ids.length !== 1 ? 's' : ''}`
                      : 'Applies to all tours'}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => handleToggle(c)}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-secondary-600 border border-secondary-300 hover:border-secondary-400 rounded-md px-3 py-1.5 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className={c.active ? 'ri-toggle-line' : 'ri-toggle-fill'} />
                  {c.active ? 'Deactivate' : 'Activate'}
                </button>
                <button
                  onClick={() => openEdit(c)}
                  className="inline-flex items-center justify-center w-9 h-9 rounded-md border border-secondary-300 text-secondary-600 hover:bg-secondary-100 transition-colors cursor-pointer"
                >
                  <i className="ri-pencil-line text-base" />
                </button>
                <button
                  onClick={() => handleDelete(c)}
                  className="inline-flex items-center justify-center w-9 h-9 rounded-md border border-red-200 text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                >
                  <i className="ri-delete-bin-line text-base" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create / edit modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setShowForm(false)}
          />
          <div className="relative bg-white rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-heading text-lg font-bold text-secondary-900">
                {isEditing ? 'Edit discount code' : 'New discount code'}
              </h3>
              <button
                onClick={() => setShowForm(false)}
                className="w-8 h-8 rounded-md flex items-center justify-center text-secondary-500 hover:bg-secondary-100 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-secondary-700 mb-1.5 block">
                  Code <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value })}
                  placeholder="e.g. SUMMER10"
                  className="w-full uppercase border border-secondary-200 rounded-md px-3.5 py-2.5 text-sm text-secondary-800 placeholder:text-secondary-400 bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-secondary-700 mb-1.5 block">
                    Type
                  </label>
                  <div className="relative">
                    <select
                      value={form.type}
                      onChange={(e) =>
                        setForm({ ...form, type: e.target.value as 'percent' | 'fixed' })
                      }
                      className="appearance-none w-full border border-secondary-200 rounded-md pl-3.5 pr-8 py-2.5 text-sm text-secondary-800 bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition cursor-pointer"
                    >
                      <option value="percent">Percent (%)</option>
                      <option value="fixed">Fixed ($)</option>
                    </select>
                    <SelectChevron className="absolute right-2.5 top-1/2 -translate-y-1/2 text-secondary-400 text-sm" />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-secondary-700 mb-1.5 block">
                    Value <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    min="1"
                    step={form.type === 'percent' ? '1' : '0.01'}
                    value={form.value}
                    onChange={(e) => setForm({ ...form, value: e.target.value })}
                    placeholder={form.type === 'percent' ? '10' : '15'}
                    className="w-full border border-secondary-200 rounded-md px-3.5 py-2.5 text-sm text-secondary-800 placeholder:text-secondary-400 bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-secondary-700 mb-1.5 block">
                    Valid until (optional)
                  </label>
                  <input
                    type="date"
                    value={form.valid_until}
                    onChange={(e) => setForm({ ...form, valid_until: e.target.value })}
                    className="w-full border border-secondary-200 rounded-md px-3.5 py-2.5 text-sm text-secondary-800 bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-secondary-700 mb-1.5 block">
                    Usage limit (optional)
                  </label>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={form.usage_limit}
                    onChange={(e) => setForm({ ...form, usage_limit: e.target.value })}
                    placeholder="Unlimited"
                    className="w-full border border-secondary-200 rounded-md px-3.5 py-2.5 text-sm text-secondary-800 placeholder:text-secondary-400 bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition"
                  />
                  <p className="mt-1 text-[11px] text-secondary-400">
                    Leave blank for unlimited uses.
                  </p>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-secondary-700 mb-1.5 block">
                  Description (optional)
                </label>
                <input
                  type="text"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="e.g. Summer promotion"
                  className="w-full border border-secondary-200 rounded-md px-3.5 py-2.5 text-sm text-secondary-800 placeholder:text-secondary-400 bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition"
                />
              </div>

              {/* Tour restriction */}
              <div>
                <label className="text-sm font-medium text-secondary-700 mb-1.5 block">
                  Apply to specific tours (optional)
                </label>
                <p className="text-[11px] text-secondary-400 mb-2">
                  Leave all unselected to apply this code to every tour.
                </p>
                <div className="border border-secondary-200 rounded-md max-h-48 overflow-y-auto divide-y divide-secondary-100">
                  {tours.map((t) => (
                    <label
                      key={t.id}
                      className="flex items-center gap-2.5 px-3.5 py-2.5 cursor-pointer hover:bg-secondary-50 transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={form.tour_ids.includes(t.id)}
                        onChange={() => toggleTour(t.id)}
                        className="w-4 h-4 rounded accent-primary-500 cursor-pointer shrink-0"
                      />
                      <span className="text-sm text-secondary-700 leading-snug">{t.title}</span>
                    </label>
                  ))}
                  {tours.length === 0 && (
                    <p className="px-3.5 py-3 text-sm text-secondary-400">No tours available.</p>
                  )}
                </div>
              </div>

              <label className="flex items-center gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.active}
                  onChange={(e) => setForm({ ...form, active: e.target.checked })}
                  className="w-4 h-4 rounded accent-primary-500 cursor-pointer"
                />
                <span className="text-sm text-secondary-700">Active (usable right away)</span>
              </label>

              {formError && (
                <div className="flex items-start gap-2 bg-red-50 border border-red-100 rounded-md px-3.5 py-2.5">
                  <i className="ri-error-warning-line text-red-500 text-sm mt-0.5 shrink-0" />
                  <p className="text-sm text-red-600">{formError}</p>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="inline-flex items-center justify-center gap-2 border border-secondary-300 hover:border-secondary-400 text-secondary-700 font-semibold text-sm px-5 py-2.5 rounded-full transition-colors whitespace-nowrap cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-60 text-white font-semibold text-sm px-5 py-2.5 rounded-full transition-colors whitespace-nowrap cursor-pointer"
                >
                  {saving ? (
                    <i className="ri-loader-4-line animate-spin text-base" />
                  ) : (
                    <>{isEditing ? 'Save changes' : 'Create code'}</>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}